import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  FormControl,
} from '@angular/forms';

import {
  EditableTableRow,
  EditableTableColumn,
  CellValueChangedEvent,
} from '../../models/irembo-editable-table.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'irembogov-editable-table',
  templateUrl: './irembo-editable-table.component.html',
  styleUrls: ['./irembo-editable-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboEditableTableComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: IremboEditableTableComponent,
      multi: true,
    },
  ],
})
export class IremboEditableTableComponent
  implements ControlValueAccessor, Validator, OnChanges
{
  @Input() rows: EditableTableRow[] = [];
  @Input() columns: EditableTableColumn[] = [];
  @Input() customTableClass = '';
  @Input() showRowLabels = false;
  @Input() allowRowAddition = false;
  @Input() allowRowDeletion = false;
  @Input() disabled = false;
  @Input() rowColumnTitle = '';
  @Input() loading = false;
  @Input() readOnly = false;
  @Input() tableData: Record<string, Record<string, any>> = {};

  @Output() cellValueChanged = new EventEmitter<CellValueChangedEvent>();
  @Output() rowAdded = new EventEmitter<string>();
  @Output() rowDeleted = new EventEmitter<string>();
  @Output() touched = new EventEmitter<void>();

  shimmerTableSize = Array.from(Array(3).keys());

  cellControls: Map<string, FormControl> = new Map();
  isTouched = false;
  rowCounter = 0;

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
  private _onValidatorChange: () => void = () => {};
  /* eslint-enable */

  constructor(
    private cd: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows'] || changes['columns']) {
      this.initializeCellControls();
    }
  }

  writeValue(value: Record<string, Record<string, any>>): void {
    if (value && typeof value === 'object') {
      this.tableData = { ...value };
      this.updateCellControls();
      this._onValidatorChange();
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cellControls.forEach(control => {
      if (isDisabled) {
        control.disable();
      } else {
        control.enable();
      }
    });
    this.cd.detectChanges();
  }

  validate(): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};
    let hasError = false;

    this.rows.forEach(row => {
      this.columns.forEach(column => {
        const cellKey = this.getCellKey(row.key, column.key);
        const control = this.cellControls.get(cellKey);

        if (control && control.invalid) {
          hasError = true;
          if (!validationErrors['cells']) {
            validationErrors['cells'] = {};
          }
          validationErrors['cells'][cellKey] = control.errors;
        }
      });
    });

    if (hasError) {
      validationErrors['custom'] = this.translate.instant(
        'Please correct the invalid cells in the table'
      );
    }

    return hasError ? validationErrors : null;
  }

  markAsTouched(): void {
    if (!this.isTouched) {
      this.isTouched = true;
      this._onTouched();
      this.touched.emit();
    }
  }

  private initializeCellControls(): void {
    this.cellControls.clear();

    this.rows.forEach(row => {
      this.columns.forEach(column => {
        const cellKey = this.getCellKey(row.key, column.key);
        const control = new FormControl(
          this.tableData[row.key]?.[column.key],
          this.getValidators(column)
        );

        if (this.disabled || row.disabled || column.disabled) {
          control.disable();
        }

        this.cellControls.set(cellKey, control);
      });
    });
  }

  private updateCellControls(): void {
    this.rows.forEach(row => {
      this.columns.forEach(column => {
        const cellKey = this.getCellKey(row.key, column.key);
        const control = this.cellControls.get(cellKey);

        if (control) {
          const value = this.tableData[row.key]?.[column.key];
          control.setValue(value, { emitEvent: false });
        }
      });
    });
    this.cd.detectChanges();
  }

  private getValidators(column: EditableTableColumn): any[] {
    const validators = [];

    if (column.required) {
      validators.push((control: FormControl) => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
          return { required: true };
        }
        return null;
      });
    }

    if (column.pattern) {
      validators.push((control: FormControl) => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
          return null;
        }
        const regex = new RegExp(column.pattern!);
        return regex.test(value) ? null : { pattern: true };
      });
    }

    if (column.type === 'number') {
      if (column.min !== undefined) {
        validators.push((control: FormControl) => {
          const value = control.value;
          if (value === null || value === undefined) {
            return null;
          }
          return value >= column.min! ? null : { min: true };
        });
      }
      if (column.max !== undefined) {
        validators.push((control: FormControl) => {
          const value = control.value;
          // Skip validation only for null or undefined, NOT for 0
          if (value === null || value === undefined) {
            return null;
          }
          return value <= column.max! ? null : { max: true };
        });
      }
    }

    return validators;
  }

  getCellKey(rowKey: string, columnKey: string): string {
    return `${rowKey}_${columnKey}`;
  }

  getCellControl(rowKey: string, columnKey: string): FormControl | undefined {
    return this.cellControls.get(this.getCellKey(rowKey, columnKey));
  }

  isCellDisabled(row: EditableTableRow, column: EditableTableColumn): boolean {
    return this.disabled || row.disabled || column.disabled || false;
  }

  onCellValueChange(rowKey: string, columnKey: string, event: Event): void {
    this.markAsTouched();

    const target = event.target as HTMLInputElement | HTMLSelectElement;
    let value: any = target.value;

    const column = this.columns.find(c => c.key === columnKey);

    if (column?.type === 'number') {
      value = value ? parseFloat(value) : null;
    }

    if (!this.tableData[rowKey]) {
      this.tableData[rowKey] = {};
    }
    this.tableData[rowKey][columnKey] = value;

    const cellKey = this.getCellKey(rowKey, columnKey);
    const control = this.cellControls.get(cellKey);
    if (control) {
      control.setValue(value);
      control.markAsTouched();
    }

    this.cellValueChanged.emit({ rowKey, columnKey, value });
    this._onChange(this.tableData);
    this._onValidatorChange();
  }

  addRow(): void {
    if (!this.allowRowAddition) return;

    this.rowCounter++;
    const newRowKey = `row_${Date.now()}_${this.rowCounter}`;
    const newRow: EditableTableRow = {
      key: newRowKey,
      label: `Row ${this.rows.length + 1}`,
    };

    this.rows.push(newRow);
    this.tableData[newRowKey] = {};

    this.columns.forEach(column => {
      const cellKey = this.getCellKey(newRowKey, column.key);
      const control = new FormControl(null, this.getValidators(column));
      this.cellControls.set(cellKey, control);
    });

    this.rowAdded.emit(newRowKey);
    this._onChange(this.tableData);
    this.cd.detectChanges();
  }

  deleteRow(rowKey: string): void {
    if (!this.allowRowDeletion) return;

    const rowIndex = this.rows.findIndex(r => r.key === rowKey);
    if (rowIndex > -1) {
      this.rows.splice(rowIndex, 1);
      delete this.tableData[rowKey];

      this.columns.forEach(column => {
        const cellKey = this.getCellKey(rowKey, column.key);
        this.cellControls.delete(cellKey);
      });

      this.rowDeleted.emit(rowKey);
      this._onChange(this.tableData);
      this.cd.detectChanges();
    }
  }

  getCellValue(rowKey: string, columnKey: string): any {
    return this.tableData[rowKey]?.[columnKey];
  }

  isCellInvalid(rowKey: string, columnKey: string): boolean {
    const control = this.getCellControl(rowKey, columnKey);
    return control ? control.invalid && control.touched : false;
  }

  getCellErrorMessage(rowKey: string, columnKey: string): string {
    const control = this.getCellControl(rowKey, columnKey);
    if (!control || !control.errors) return '';

    if (control.errors['required']) {
      return this.translate.instant('Required');
    }

    if (control.errors['pattern']) {
      return this.translate.instant('Invalid format');
    }

    if (control.errors['min']) {
      const minValue = this.columns.find(c => c.key === columnKey)?.min;
      return `${this.translate.instant('Min value')}: ${minValue}`;
    }

    if (control.errors['max']) {
      const maxValue = this.columns.find(c => c.key === columnKey)?.max;
      return `${this.translate.instant('Max value')}: ${maxValue}`;
    }

    return this.translate.instant('Invalid');
  }

  onDropdownCellValueChange(
    rowKey: string,
    columnKey: string,
    value: any
  ): void {
    this.markAsTouched();

    if (!this.tableData[rowKey]) {
      this.tableData[rowKey] = {};
    }
    this.tableData[rowKey][columnKey] = value;

    const cellKey = this.getCellKey(rowKey, columnKey);
    const control = this.cellControls.get(cellKey);
    if (control) {
      control.setValue(value);
      control.markAsTouched();
    }

    this.cellValueChanged.emit({ rowKey, columnKey, value });
    this._onChange(this.tableData);
    this._onValidatorChange();
  }

  getCellDisplayValue(
    rowKey: string,
    columnKey: string,
    column: EditableTableColumn
  ): string {
    if (!this.tableData || !this.tableData[rowKey]) {
      return '-';
    }

    const cellValue = this.tableData[rowKey][columnKey];

    if (cellValue === null || cellValue === undefined || cellValue === '') {
      return '-';
    }

    if (column.type === 'select' && column.options) {
      const selectedOption = column.options.find(
        opt => opt.value === cellValue
      );
      if (selectedOption) {
        return selectedOption.label;
      }
    }

    if (column.type === 'date' && cellValue) {
      try {
        const date = new Date(cellValue);
        return date.toLocaleDateString();
      } catch (e) {
        return String(cellValue);
      }
    }

    return String(cellValue);
  }
}
